import { useEffect, useRef } from "react";
import * as Y from "yjs";

/**
 * Sync Bridge: watches BlockNote editor blocks and mirrors qualifying blocks
 * into the flat nodesMap so they appear in the tree view.
 *
 * Currently syncs: heading blocks with level 1 (H1).
 * Future: configurable filter by block type, heading level, or custom blocks.
 *
 * Each synced block uses BlockNote's native block ID as the nodesMap key,
 * ensuring the tree and BlockNote reference the same entity.
 */
export function useSyncBridge(
  editor: any,
  doc: Y.Doc,
  options?: { headingLevel?: number },
) {
  const level = options?.headingLevel ?? 1;
  const prevSyncedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!editor) return;

    const sync = () => {
      const nodesMap = doc.getMap("nodes");
      const blocks: any[] = editor.document ?? [];
      const currentIds = new Set<string>();

      // Collect all qualifying blocks from BlockNote
      const qualifying: { id: string; text: string; type: string }[] = [];

      const walk = (blockList: any[]) => {
        for (const block of blockList) {
          if (block.type === "heading" && block.props?.level === level) {
            // Extract plain text from inline content
            const text = (block.content ?? [])
              .map((c: any) => (typeof c === "string" ? c : (c.text ?? "")))
              .join("")
              .trim();

            qualifying.push({ id: block.id, text, type: "heading" });
            currentIds.add(block.id);
          }

          // Also sync custom block types (e.g., character-sheet)
          if (block.type === "character-sheet") {
            qualifying.push({
              id: block.id,
              text: block.props?.name ?? "Token",
              type: "character-sheet",
            });
            currentIds.add(block.id);
          }

          // Recurse into children
          if (block.children?.length) {
            walk(block.children);
          }
        }
      };

      walk(blocks);

      doc.transact(() => {
        const edgesMap = doc.getMap("edges");
        const DOC_ROOT_ID = "__document_root__";

        // Upsert qualifying blocks into nodesMap
        for (const q of qualifying) {
          const existing = nodesMap.get(q.id) as Y.Map<unknown> | undefined;
          if (existing) {
            // Update name if changed
            const currentName = existing.get("name") as string | undefined;
            if (currentName !== q.text && q.text) {
              existing.set("name", q.text);
            }
          } else {
            // Create new node mirroring the BlockNote block
            const nodeMap = new Y.Map<unknown>();
            nodeMap.set("id", q.id);
            nodeMap.set("type", q.type);
            nodeMap.set("name", q.text || "Untitled");
            nodeMap.set("_syncSource", "blocknote");
            nodesMap.set(q.id, nodeMap);

            // Create hierarchy edge: document root → this block
            const edgeId = `_sync_edge_${q.id}`;
            if (!edgesMap.has(edgeId)) {
              const edgeMap = new Y.Map<unknown>();
              edgeMap.set("source_id", DOC_ROOT_ID);
              edgeMap.set("target_id", q.id);
              edgeMap.set("type", "hierarchy");
              edgesMap.set(edgeId, edgeMap);
            }
          }
        }

        // Remove nodes that were previously synced but no longer qualify
        for (const oldId of prevSyncedIds.current) {
          if (!currentIds.has(oldId)) {
            const node = nodesMap.get(oldId) as Y.Map<unknown> | undefined;
            if (node && node.get("_syncSource") === "blocknote") {
              nodesMap.delete(oldId);
              // Also remove the hierarchy edge
              const edgeId = `_sync_edge_${oldId}`;
              if (edgesMap.has(edgeId)) {
                edgesMap.delete(edgeId);
              }
            }
          }
        }
      });

      prevSyncedIds.current = currentIds;
    };

    // Run initial sync
    sync();

    // Subscribe to changes
    editor.onChange(sync);

    // BlockNote doesn't return an unsubscribe from onChange in all versions,
    // so we rely on the effect cleanup unmounting the component.
    // The editor instance will be destroyed when the component unmounts.
  }, [editor, doc, level]);
}
